import type { TypographyVariant } from '../primitives/index.js'
import { Typography } from '../primitives/index.js'
import { Home } from '@tunasong/icons'
import type { NavigationEntityWithType } from '@tunasong/models'
import { getElementMedia, usePlugins } from '@tunasong/plugin-lib'
import type { FC, HTMLAttributes } from 'react'
import { Fragment, useEffect, useMemo, useState } from 'react'
import { Link as RouterLink } from 'react-router'
import { getEntityPath } from '../entity/entity-link-util.js'
import { useContainerSize, useHover } from '../hooks/index.js'
import styles from './breadcrumbs.module.css'

export interface TunaBreadcrumbsProps extends HTMLAttributes<HTMLElement> {
  className?: string
  parentChain: NavigationEntityWithType[]
  variant?: TypographyVariant
  icon?: boolean
  /** Show the Home icon. @default true */
  showHome?: boolean
  /** Show the leaf - i.e., the entity. @default true */
  showLeaf?: boolean
  /** Make the links active, i.e., clickable. @default true */
  activeLinks?: boolean
  back?: boolean
  maxItems?: number
}

export const TunaBreadcrumbs: FC<TunaBreadcrumbsProps> = props => {
  const {
    className,
    parentChain = [],
    activeLinks = true,
    variant = 'subtitle1',
    icon = true,
    showHome = true,
    showLeaf = true,
    maxItems = -1,
  } = props

  const plugins = usePlugins('all')

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const [expanded, setExpanded] = useState(false)
  const size = useContainerSize(anchorEl)
  const hover = useHover(anchorEl)

  // Reset expanded state when navigating to a different entity
  const leafId = parentChain[parentChain.length - 1]?.id
  useEffect(() => setExpanded(false), [leafId])

  const path = useMemo(() => {
    const p = [...parentChain]
    const trimmed = showLeaf ? p : p.slice(0, p.length - 1)
    return trimmed
  }, [parentChain, showLeaf])

  const links = useMemo(
    () =>
      path.map((e, idx) => {
        const { colorCode, icon: Icon } = getElementMedia(e.type, plugins)
        const content = (
          <Fragment>
            {icon ? (
              <div style={{ color: hover ? colorCode : 'var(--tuna-palette-action-disabled)' }}>
                <Icon
                  size={20}
                  className={styles.iconWrapper}
                  style={{
                    color: hover ? colorCode : 'var(--tuna-palette-action-disabled)',
                  }}
                />
              </div>
            ) : null}
            {e.name ? e.name : 'Untitled'}
          </Fragment>
        )
        return activeLinks ? (
          <RouterLink key={idx} className={styles.link} to={getEntityPath({ entity: e })}>
            {content}
          </RouterLink>
        ) : (
          <Typography variant={variant} color="inherit" key={idx} className={styles.inactiveLink}>
            {content}
          </Typography>
        )
      }),
    [activeLinks, hover, icon, path, plugins, variant]
  )

  const maxTotalItems = size && maxItems === -1 ? Math.max(Math.ceil(size.width / 175), 2) : Math.max(maxItems, 3)

  const visibleLinks = useMemo(() => {
    const allItems = [
      ...(showHome
        ? [
            <RouterLink key="home" className={styles.homeLink} to="/app" title="Home link" aria-label="Home link">
              {icon ? <Home size={24} /> : null}
            </RouterLink>,
          ]
        : []),
      ...links,
    ]
    if (!expanded && maxTotalItems < allItems.length) {
      return [
        ...allItems.slice(0, 1),
        <span key="ellipsis" className={styles.ellipsis} role="button" onClick={() => setExpanded(true)}>
          ...
        </span>,
        ...allItems.slice(-Math.max(1, maxTotalItems - 2)),
      ]
    }
    return allItems
  }, [expanded, showHome, icon, links, maxTotalItems])

  const navClasses = [styles.nav, className].filter(Boolean).join(' ')

  return (
    <nav ref={setAnchorEl} className={navClasses} aria-label="breadcrumb" style={props.style}>
      {visibleLinks.map((item, i) => (
        <Fragment key={i}>
          {i > 0 ? <span className={styles.separator}>/</span> : null}
          {item}
        </Fragment>
      ))}
    </nav>
  )
}
